/**
 * Inventories Table Component
 * @see US-12.6 - Page Liste
 * @see sprint-datagrid-migration Phase 1.5
 */

import { useMemo, useCallback } from 'react';
import { Check, X, Trash2, MoreVertical, Play, FileText, FileSpreadsheet } from 'lucide-react';
import {
  DataGrid,
  Badge,
  Dropdown,
  DropdownItem,
  textColumn,
  dateTimeColumn,
  integerColumn,
  moneyColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import type {
  InventoryListItem,
  QueryInventoriesDto,
} from '@bstock/shared';
import {
  InventoryStatus,
  InventoryType,
  INVENTORY_STATUS_LABELS,
  INVENTORY_STATUS_COLORS,
  INVENTORY_TYPE_LABELS,
} from '@bstock/shared';

type SortField = QueryInventoriesDto['sortBy'];
type SortOrder = QueryInventoriesDto['sortOrder'];

interface InventoriesTableProps {
  inventories: InventoryListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onRowClick: (inventory: InventoryListItem) => void;
  onContinue: (inventory: InventoryListItem) => void;
  onValidate?: (inventory: InventoryListItem) => void;
  onCancel?: (inventory: InventoryListItem) => void;
  onDelete?: (inventory: InventoryListItem) => void;
  onDownloadSheet?: (inventoryId: string, format: 'pdf' | 'excel') => void;
  onSort: (field: SortField, order: SortOrder) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  showFinancials?: boolean;
}

function getStatusVariant(status: InventoryStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  const colorMap: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    green: 'success',
    gray: 'neutral',
    orange: 'warning',
    red: 'danger',
  };
  return colorMap[INVENTORY_STATUS_COLORS[status]] ?? 'neutral';
}

const SORT_FIELD_MAP: Record<string, SortField> = {
  inventoryNumber: 'inventoryNumber',
  createdAt: 'createdAt',
  validatedAt: 'validatedAt',
};

export function InventoriesTable({
  inventories,
  pagination,
  isLoading,
  sortBy,
  sortOrder,
  onRowClick,
  onContinue,
  onValidate,
  onCancel,
  onDelete,
  onDownloadSheet,
  onSort,
  onPageChange,
  onLimitChange,
  showFinancials = true,
}: InventoriesTableProps) {
  const currency = useCurrency();
  const hasAnyAction = onValidate || onCancel || onDelete || onDownloadSheet;

  const columns = useMemo<DataGridColumn<InventoryListItem>[]>(() => {
    const cols: DataGridColumn<InventoryListItem>[] = [
      textColumn<InventoryListItem>({
        id: 'inventoryNumber',
        header: 'Numéro',
        accessor: 'inventoryNumber',
        cellClassName: 'font-mono',
      }),
      dateTimeColumn<InventoryListItem>({
        id: 'createdAt',
        header: 'Création',
        accessor: (r) => new Date(r.createdAt),
      }),
      dateTimeColumn<InventoryListItem>({
        id: 'validatedAt',
        header: 'Validation',
        accessor: (r) => (r.validatedAt ? new Date(r.validatedAt) : null),
      }),
      dateTimeColumn<InventoryListItem>({
        id: 'effectiveDate',
        header: "Date d'effet",
        sortable: false,
        accessor: (r) =>
          new Date(r.effectiveDate ?? r.validatedAt ?? r.createdAt),
      }),
      textColumn<InventoryListItem>({
        id: 'type',
        header: 'Type',
        sortable: false,
        accessor: (r) => INVENTORY_TYPE_LABELS[r.type as InventoryType],
      }),
      textColumn<InventoryListItem>({
        id: 'name',
        header: 'Nom',
        sortable: false,
        accessor: 'name',
        ellipsis: true,
        maxWidth: '180px',
      }),
      textColumn<InventoryListItem>({
        id: 'createdByUser',
        header: 'Créé par',
        sortable: false,
        accessor: (r) => `${r.createdByUser.firstName} ${r.createdByUser.lastName}`,
      }),
      // Progress bar — custom cell, no dataType needed
      textColumn<InventoryListItem>({
        id: 'progress',
        header: 'Progression',
        sortable: false,
        exportValue: (r) => {
          const pct =
            r.linesCount > 0
              ? Math.round((r.countedLinesCount / r.linesCount) * 100)
              : 0;
          return `${pct}%`;
        },
        cell: (r) => {
          const pct =
            r.linesCount > 0
              ? Math.round((r.countedLinesCount / r.linesCount) * 100)
              : 0;
          return (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-neutral-500 w-10 text-right">
                {pct}%
              </span>
            </div>
          );
        },
      }),
      integerColumn<InventoryListItem>({
        id: 'variancesCount',
        header: 'Écarts',
        sortable: false,
        align: 'center',
        accessor: 'variancesCount',
        cell: (r) => (
          <span
            className={
              r.variancesCount > 0
                ? 'text-danger-600 font-medium'
                : 'text-neutral-500'
            }
          >
            {r.variancesCount}
          </span>
        ),
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<InventoryListItem>({
          id: 'totalVarianceValue',
          header: 'Valeur écart',
          sortable: false,
          accessor: (r) => r.totalVarianceValue / 100,
          cell: (r) => {
            const value = r.totalVarianceValue;
            if (value === 0) return <span className="text-neutral-500">—</span>;
            const formatted = formatCentimes(Math.abs(value), currency);
            return (
              <span
                className={value < 0 ? 'text-danger-600' : 'text-success-600'}
              >
                {value < 0 ? '-' : '+'}
                {formatted}
              </span>
            );
          },
        })
      );
    }

    cols.push(
      textColumn<InventoryListItem>({
        id: 'status',
        header: 'Statut',
        align: 'center',
        sortable: false,
        exportValue: (r) => INVENTORY_STATUS_LABELS[r.status],
        cell: (r) => (
          <Badge variant={getStatusVariant(r.status)}>
            {INVENTORY_STATUS_LABELS[r.status]}
          </Badge>
        ),
      })
    );

    if (hasAnyAction) {
      cols.push(
        textColumn<InventoryListItem>({
          id: 'actions',
          header: '',
          sortable: false,
          exportSkip: true,
          width: '50px',
          align: 'center',
          cell: (r) => {
            const isDraft = r.status === InventoryStatus.DRAFT;
            const isInProgress = r.status === InventoryStatus.IN_PROGRESS;
            const isValidated = r.status === InventoryStatus.VALIDATED;
            const showContinue = isDraft || isInProgress;
            const showValidate = onValidate && (isDraft || isInProgress);
            const showDelete = onDelete && (isDraft || isInProgress);
            const showCancel = onCancel && isValidated;
            const showSheet = onDownloadSheet && (isDraft || isInProgress);
            if (!showContinue && !showValidate && !showDelete && !showCancel && !showSheet)
              return null;

            return (
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown
                  trigger={
                    <button className="p-1 rounded hover:bg-neutral-100">
                      <MoreVertical className="w-4 h-4 text-neutral-500" />
                    </button>
                  }
                >
                  {showContinue && (
                    <DropdownItem
                      icon={<Play className="w-4 h-4" />}
                      onClick={() => onContinue(r)}
                    >
                      {isDraft ? 'Commencer' : 'Continuer'}
                    </DropdownItem>
                  )}
                  {showValidate && (
                    <DropdownItem
                      icon={<Check className="w-4 h-4" />}
                      onClick={() => onValidate(r)}
                    >
                      Valider
                    </DropdownItem>
                  )}
                  {showSheet && (
                    <DropdownItem
                      icon={<FileText className="w-4 h-4" />}
                      onClick={() => onDownloadSheet(r.id, 'pdf')}
                    >
                      Feuille saisie PDF
                    </DropdownItem>
                  )}
                  {showSheet && (
                    <DropdownItem
                      icon={<FileSpreadsheet className="w-4 h-4" />}
                      onClick={() => onDownloadSheet(r.id, 'excel')}
                    >
                      Feuille saisie Excel
                    </DropdownItem>
                  )}
                  {showDelete && (
                    <DropdownItem
                      icon={<Trash2 className="w-4 h-4" />}
                      onClick={() => onDelete(r)}
                      className="text-danger-600 hover:bg-danger-50"
                    >
                      Supprimer
                    </DropdownItem>
                  )}
                  {showCancel && (
                    <DropdownItem
                      icon={<X className="w-4 h-4" />}
                      onClick={() => onCancel(r)}
                      className="text-danger-600 hover:bg-danger-50"
                    >
                      Annuler
                    </DropdownItem>
                  )}
                </Dropdown>
              </div>
            );
          },
        })
      );
    }

    return cols;
  }, [
    currency,
    onContinue,
    onValidate,
    onCancel,
    onDelete,
    onDownloadSheet,
    showFinancials,
    hasAnyAction,
  ]);

  // ---- Sort (server-side, controlled) ------------------------------------
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(
      ([, v]) => v === sortBy
    )?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!next) {
        onSort('createdAt', 'desc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort]
  );

  return (
    <DataGrid<InventoryListItem>
      data={inventories}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={8}
      onRowClick={onRowClick}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'inventaires' },
        sheetName: 'Inventaires',
        position: 'header',
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucun inventaire trouvé
        </div>
      }
    />
  );
}
